import React, { useState, useEffect, useCallback } from 'react';
import { 
  Paper, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, 
  Box, Chip, IconButton, Toolbar, Typography, Grid, InputAdornment, 
  Select, MenuItem, FormControl, InputLabel, alpha, Rating,
  Table, TableHead, TableBody, TableCell, TableRow,
  CircularProgress
} from '@mui/material';
import Container from '@mui/material/Container';
import TableContainer from '@mui/material/TableContainer';
import { 
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useColorMode } from '../../../theme/ThemeContext';
import { useTheme } from '@mui/material/styles';
import axios from "axios";
import { useAuth } from "../../../utils/AuthContext";
import Swal from "sweetalert2";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaUsers,
  FaUserPlus,
  FaUserMinus,
} from "react-icons/fa";
import TableComponent from '../admincomponents/Table';

const GOLD = "#D4AF37";

const InstructorManagement = () => {
  const { toggleColorMode, mode } = useColorMode();
  const theme = useTheme();
  const { token } = useAuth();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentInstructor, setCurrentInstructor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile_number: "",
    password: "",
    role_id: 3,
  });

  const fetchingRef = React.useRef(false);

  const fetchInstructors = useCallback(async () => {
    if (!token || fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const res = await axios.get("https://tdtlworld.com/dss-kaas-backend/lms/all-users/", {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let rawUsers = [];
      if (Array.isArray(res.data)) {
        rawUsers = res.data;
      } else if (res.data && Array.isArray(res.data.users)) {
        rawUsers = res.data.users;
      } else if (res.data && typeof res.data === 'object') {
        rawUsers = res.data.data || [];
      }

      const instructorUsers = (Array.isArray(rawUsers) ? rawUsers : [])
        .map((user) => ({
          ...user,
          id: user.user_id || user.id || Math.random(),
          first_name: user.first_name || user.name?.split(' ')[0] || user.username || "Unknown",
          last_name: user.last_name || user.name?.split(' ').slice(1).join(' ') || "",
          email: user.email || "N/A",
          status: user.status || "Active",
          role_id: user.role_id,
          role: user.role || "N/A",
        }));

      setInstructors(instructorUsers);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [token]);
  
  useEffect(() => {
    fetchInstructors();
  }, [fetchInstructors]);

  useEffect(() => {
    // TEMPORARY MOCK DATA FOR DEBUGGING
    if (instructors.length === 0 && !loading) {
      console.log("No instructors found, adding mock data for debugging");
      setInstructors([
        { 
          user_id: 999, 
          first_name: "DEBUG", 
          last_name: "INSTRUCTOR", 
          email: "debug@test.com", 
          status: "Active", 
          role_id: 3 
        }
      ]);
    }
  }, [instructors, loading]);

  const filteredInstructors = (instructors || []).filter((inst) => {
    const search = (searchTerm || "").toLowerCase();
    const firstName = (inst.first_name || "").toLowerCase();
    const lastName = (inst.last_name || "").toLowerCase();
    const email = (inst.email || "").toLowerCase();
    
    const matchesSearch = firstName.includes(search) || 
                         lastName.includes(search) || 
                         email.includes(search);
    const matchesStatus = !filterStatus || inst.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      key: "srno",
      label: "Sr No.",
      sortable: false,
      render: (row, index) => (
        <span style={{ color: mode === 'dark' ? '#B3B3B3' : 'text.secondary', fontWeight: 600 }}>
          {index + 1}
        </span>
      ),
    },
    {
      key: "name",
      label: "Instructor Name",
      sortable: true,
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {row.first_name} {row.last_name}
        </Typography>
      ),
    },
    { 
      key: "email", 
      label: "Email Address", 
      sortable: true,
      render: (row) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {row.email}
        </Typography>
      )
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      align: 'center',
      render: (row) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <IconButton 
            size="small" 
            onClick={() => handleEditClick(row)}
            sx={{ color: '#7366ff', bgcolor: alpha('#7366ff', 0.1), '&:hover': { bgcolor: alpha('#7366ff', 0.2) } }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => handleDeleteClick(row)}
            sx={{ color: '#ff5370', bgcolor: alpha('#ff5370', 0.1), '&:hover': { bgcolor: alpha('#ff5370', 0.2) } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleEditClick = (instructor) => {
    setCurrentInstructor(instructor);
    setFormData({
      first_name: instructor.first_name || "",
      last_name: instructor.last_name || "",
      email: instructor.email || "",
      mobile_number: instructor.mobile_number || "",
      password: "",
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (instructor) => {
    setCurrentInstructor(instructor);
    setShowDeleteModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        "https://tdtlworld.com/dss-kaas-backend/auth/admin/create-user/",
        { ...formData, role_id: 3 }
      );
      Swal.fire("Success", "Instructor added successfully!", "success");
      setShowAddModal(false);
      setFormData({
        email: "",
        password: "",
        first_name: "",
        last_name: "",
        mobile_number: "",
        role_id: 3,
      });
      fetchInstructors();
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to add instructor", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.patch(
        `https://tdtlworld.com/dss-kaas-backend/lms/users-update/${currentInstructor.user_id}/`,
        formData
      );
      Swal.fire("Success", "Instructor updated successfully!", "success");
      setShowEditModal(false);
      fetchInstructors();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to update instructor", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await axios.delete(
        `https://tdtlworld.com/dss-kaas-backend/lms/users-delete/${currentInstructor.user_id}/`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      Swal.fire("Deleted!", "Instructor has been deleted.", "success");
      setShowDeleteModal(false);
      fetchInstructors();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to delete instructor", "error");
    } finally {
      setLoading(false);
    }
  };

  const modalBackdropStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    backdropFilter: "blur(4px)",
  };

  const modalContentStyle = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "24px",
    padding: "32px",
    width: "500px",
    maxWidth: "90%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: mode === 'dark' ? "0 20px 50px rgba(0,0,0,0.5)" : "0 20px 50px rgba(0,0,0,0.15)",
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary
  };



  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          mb: 3,
          color: theme.palette.text.primary,
          fontFamily: "'Outfit', sans-serif",
        }}
      >
        Instructor{" "}
        <Box component="span" sx={{ color: GOLD }}>
          Management
        </Box>
      </Typography>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or occupation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            <IconButton 
              onClick={fetchInstructors} 
              sx={{ color: GOLD, bgcolor: alpha(GOLD, 0.1), '&:hover': { bgcolor: alpha(GOLD, 0.2) } }}
              title="Refresh List"
            >
              <RefreshIcon />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddModal(true)}
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                background: "linear-gradient(45deg, #D4AF37 30%, #F5D76E 90%)",
                boxShadow: "0 4px 15px rgba(212, 175, 55, 0.3)",
                color: "#0D0D0D",
                '&:hover': {
                  background: "linear-gradient(45deg, #F5D76E 30%, #D4AF37 90%)",
                  boxShadow: "0 6px 20px rgba(212, 175, 55, 0.5)",
                }
              }}
            >
              Add Instructor
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Box sx={{ 
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden'
      }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <TableComponent
            columns={columns}
            data={filteredInstructors}
            pagination={true}
            itemsPerPage={10}
          />
        )}
      </Box>

      {/* Add Instructor Modal */}
      {showAddModal && (
        <div style={modalBackdropStyle}>
          <div style={modalContentStyle}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 800 }}>Add New Instructor</Typography>
            <form onSubmit={handleAddSubmit}>
                  <TextField
                    fullWidth
                    name="first_name"
                    label="First Name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    sx={{ mb: 2 }}
                    variant="outlined"
                  />

              <TextField
                fullWidth
                name="last_name"
                label="Last Name"
                value={formData.last_name}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                name="mobile_number"
                label="Mobile Number"
                value={formData.mobile_number}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                name="password"
                label="Password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                sx={{ mb: 3 }}
                variant="outlined"
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 2
                }}
              >
                <Button
                  onClick={() => setShowAddModal(false)}
                  sx={{ color: 'text.secondary', fontWeight: 600 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    borderRadius: "50px",
                    background: "linear-gradient(45deg, #D4AF37 30%, #F5D76E 90%)",
                    color: "#0D0D0D",
                    fontWeight: 700,
                    px: 3,
                    '&:hover': {
                      background: "linear-gradient(45deg, #F5D76E 30%, #D4AF37 90%)",
                    }
                  }}
                >
                  Add Instructor
                </Button>
              </Box>
            </form>
          </div>
        </div>
      )}

      {/* Edit Instructor Modal */}
      {showEditModal && (
        <div style={modalBackdropStyle}>
          <div style={modalContentStyle}>
            <h2 style={{ marginBottom: "20px" }}>Edit Instructor</h2>
            <form onSubmit={handleEditSubmit}>
                  <TextField
                    fullWidth
                    name="first_name"
                    label="First Name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    sx={{ mb: 2 }}
                    variant="outlined"
                  />
              <TextField
                fullWidth
                name="last_name"
                label="Last Name"
                value={formData.last_name}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                name="phone"
                label="Phone"
                value={formData.phone}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                name="password"
                label="Password (leave blank to keep current)"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                sx={{ mb: 3 }}
                variant="outlined"
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 1
                }}
              >
                <Button
                  onClick={() => setShowEditModal(false)}
                  sx={{ color: 'text.secondary', fontWeight: 600 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    borderRadius: "50px",
                    background: "linear-gradient(45deg, #D4AF37 30%, #F5D76E 90%)",
                    color: "#0D0D0D",
                    fontWeight: 700,
                    px: 3,
                    '&:hover': {
                      background: "linear-gradient(45deg, #F5D76E 30%, #D4AF37 90%)",
                    }
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </form>
          </div>
        </div>
      )}

      {/* Delete Instructor Modal */}
      {showDeleteModal && (
        <div style={modalBackdropStyle}>
          <div style={{ ...modalContentStyle, width: "400px" }}>
            <h2 style={{ marginBottom: "20px" }}>Confirm Delete</h2>
            <p style={{ marginBottom: "20px" }}>
              Are you sure you want to delete the instructor{" "}
              <strong>{currentInstructor?.name}</strong>? This action cannot be
              undone.
            </p>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                onClick={() => setShowDeleteModal(false)}
                sx={{ color: 'text.secondary', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                variant="contained"
                color="error"
                sx={{
                  borderRadius: "50px",
                  fontWeight: 700,
                  px: 3,
                }}
              >
                Delete
              </Button>
            </Box>
          </div>
        </div>
      )}
    </Container>
  );
};

export default InstructorManagement;
